import React from 'react';
import { Box, Chip, Stack, Tooltip, Typography } from '@mui/material';
import type { RosterHour, RosterShiftSegment, StatePriorityEntry } from '../../types/contactOptimization';

/**
 * The roster Gantt grid, rewritten 2026-08-12 to draw shifts on a
 * CONTINUOUS timeline rather than as one colored box per whole hour.
 *
 * The old grid gave each hour a single status ("on"/"break"/"off"), which
 * cannot represent the thing a reader most wants to check on a roster:
 * how long a break actually is. The shortest break the solver can pick is
 * 30 minutes (SPLIT_BREAK_OPTIONS), and every break lands mid-hour, so a
 * 30-minute break rendered as a FULL hour of break and swallowed the real
 * work sharing that hour -- e.g. an agent with a 1h 30m break at
 * 11:30am-1:00pm showed two solid hours of "Break" with no way to tell
 * whether that meant 30 minutes or two hours. Segments carry the solver's
 * own half-hour-precise boundaries (roster_optimizer._segments_from_pattern),
 * so a 30-minute break is drawn 30 minutes wide and labelled as such.
 *
 * Three things are stated outright rather than left to be inferred from
 * bar geometry, because each one was a real question a reader could not
 * answer from the old grid:
 *   - each agent's WORK hours vs. the BREAK time added on top, and the
 *     resulting elapsed day, which is longer than the day they work today
 *     (the optimizer treats their current span as work time and adds
 *     breaks around it -- see the roster_optimizer assignment comment);
 *   - the clock. Hours are the company reference clock, but an agent's
 *     home timezone can put their real start hours earlier -- a PST agent
 *     starting 8:00am ET is starting 5:00am at home, which matters a lot
 *     to whoever approves this and was previously invisible;
 *   - break length, in words, next to the bar.
 */

const COLORS = {
  workToday: '#185fa5',
  workOptimized: '#2e9e6b',
  break: '#e0a458',
  track: '#eceae4',
};

export interface RosterGridRow {
  agent_full_nm: string;
  source: 'real' | 'inferred';
  sufficient_history: boolean;
  /** Optimized (or, on the as-given view, the only) shift geometry. */
  segments: RosterShiftSegment[];
  /** Today's real shift, drawn as a second bar when comparing. */
  compareSegments?: RosterShiftSegment[];
  homeTz?: string | null;
  workHours?: number | null;
  breakHours?: number | null;
  badge?: string;
  /** Extra lines appended to the agent-name tooltip. */
  tooltipExtra?: string;
}

export interface RosterGridCoverageHour {
  hour: number;
  agents_on_shift: number;
  agents_on_shift_before?: number | null;
  required_agents: number | null;
  gap: number | null;
}

/** 11.5 -> "11:30am", 13 -> "1pm". */
export function fmtClock(h: number): string {
  const norm = ((h % 24) + 24) % 24;
  const hh = Math.floor(norm + 1e-6);
  const mins = Math.round((norm - hh) * 60);
  const period = hh >= 12 ? 'pm' : 'am';
  const hour12 = hh % 12 === 0 ? 12 : hh % 12;
  return mins === 0 ? `${hour12}${period}` : `${hour12}:${mins.toString().padStart(2, '0')}${period}`;
}

/** 1.5 -> "1h 30m", 0.5 -> "30m", 3 -> "3h". */
export function fmtDuration(h: number): string {
  const hh = Math.floor(h + 1e-6);
  const mm = Math.round((h - hh) * 60);
  if (hh === 0) return `${mm}m`;
  if (mm === 0) return `${hh}h`;
  return `${hh}h ${mm}m`;
}

/** UTC offset in whole hours for the timezone abbreviations the shift
 * import actually carries, relative to the Eastern reference clock. Used
 * only to show an agent their own local start/end alongside the company
 * clock -- deliberately a small fixed map rather than a real tz library,
 * since these four are the only values `agent_shift_schedule.home_tz`
 * contains and inventing broader support would imply precision (DST
 * transitions, IANA zones) this column doesn't have. */
const HOURS_BEHIND_EASTERN: Record<string, number> = { EST: 0, ET: 0, CST: 1, MST: 2, PST: 3 };

/** Hours behind Eastern, or null if we don't recognise the timezone.
 * ZERO is a real answer (an Eastern agent), distinct from null -- an
 * Eastern agent still gets their local times printed, because a row that
 * silently omitted them left the reader unable to tell whether a bare
 * "9am-7pm" was the company clock or the agent's own. */
export function localOffsetFromEastern(homeTz: string | null | undefined): number | null {
  if (!homeTz) return null;
  const offset = HOURS_BEHIND_EASTERN[homeTz.trim().toUpperCase()];
  return offset === undefined ? null : offset;
}

/** Short label for the company clock: "America/New_York" -> "ET". */
function companyClockLabel(referenceTimezone: string): string {
  return referenceTimezone.includes('New_York') ? 'ET' : referenceTimezone;
}

/** A shift that starts before 7am or ends after 9pm WHERE THE AGENT
 * LIVES. The roster places shifts on the company clock, so a perfectly
 * ordinary-looking 8am Eastern start is a 5am start for a Pacific agent
 * -- worth flagging on the row rather than leaving to be worked out. */
function isUnsociableLocal(start: number, end: number): boolean {
  return start < 7 || end > 21;
}

function segmentsSpan(segments: RosterShiftSegment[]): { start: number; end: number } | null {
  if (!segments.length) return null;
  return { start: segments[0].start, end: segments[segments.length - 1].end };
}

/** One agent's shift drawn as absolutely-positioned bars over a shared
 * hour axis -- geometry comes straight from the segment boundaries, so
 * any duration renders at its true width. */
function ShiftTrack({
  segments,
  trackStart,
  trackHours,
  height,
  workColor,
  agentName,
  scheduleLabel,
  referenceTimezone,
  localOffset,
  showBreakLabel,
}: {
  segments: RosterShiftSegment[];
  trackStart: number;
  trackHours: number;
  height: number;
  workColor: string;
  agentName: string;
  scheduleLabel: string;
  referenceTimezone: string;
  localOffset: number | null;
  showBreakLabel: boolean;
}) {
  return (
    <Box sx={{ position: 'relative', height, bgcolor: COLORS.track, borderRadius: '3px', overflow: 'hidden' }}>
      {segments.map((seg, i) => {
        const left = ((seg.start - trackStart) / trackHours) * 100;
        const width = ((seg.end - seg.start) / trackHours) * 100;
        if (width <= 0 || left >= 100 || left + width <= 0) return null;
        const duration = seg.end - seg.start;
        const isBreak = seg.kind === 'break';
        // Only worth restating when the agent's clock actually differs.
        const localSuffix =
          localOffset != null && localOffset !== 0
            ? ` — ${fmtClock(seg.start - localOffset)}–${fmtClock(seg.end - localOffset)} their local time`
            : '';
        return (
          <Tooltip
            key={i}
            arrow
            title={`${agentName} — ${scheduleLabel}: ${isBreak ? 'Break' : 'Working'} ${fmtClock(
              seg.start,
            )}–${fmtClock(seg.end)} (${fmtDuration(duration)}) ${referenceTimezone}${localSuffix}`}
          >
            <Box
              sx={{
                position: 'absolute',
                left: `${Math.max(0, left)}%`,
                width: `${Math.min(100 - Math.max(0, left), width)}%`,
                top: 0,
                height: '100%',
                bgcolor: isBreak ? COLORS.break : workColor,
                borderRadius: '3px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                // A break is a hole in the shift, not more shift -- the
                // inset ring keeps it reading as a gap even where it sits
                // flush between two work blocks.
                boxShadow: isBreak ? 'inset 0 0 0 1px rgba(255,255,255,0.85)' : 'none',
              }}
            >
              {/* Every break is labelled, never only the wide ones --
                  break LENGTH is the whole reason this grid was redrawn,
                  and a 30-minute break (the shortest the solver can pick,
                  and the most common one in practice) is the narrowest
                  bar here, so suppressing labels below a width threshold
                  hid the exact case that most needed the text. A bar too
                  narrow to hold "30m" inside gets it as a pill that
                  overflows onto the neighbouring work blocks instead:
                  the timeline geometry stays true to scale, which is the
                  property worth protecting, and only the label spills. */}
              {isBreak && showBreakLabel && width > 4.5 && (
                <Typography
                  sx={{ fontSize: 9, fontWeight: 700, color: '#5b3a06', lineHeight: 1, whiteSpace: 'nowrap' }}
                >
                  {fmtDuration(duration)}
                </Typography>
              )}
            </Box>
          </Tooltip>
        );
      })}
      {/* Hour gridlines, drawn ON TOP of the bars rather than beneath
          them. Underneath, they were hidden by the very bars whose edges
          a reader is trying to line up against the axis -- which is what
          made the grid hard to read a start time off. The tone is a
          translucent dark so the same line stays visible over a green
          block, an amber break, and the empty track alike. */}
      {Array.from({ length: trackHours - 1 }, (_, i) => (
        <Box
          key={`grid-${i}`}
          sx={{
            position: 'absolute',
            left: `${((i + 1) / trackHours) * 100}%`,
            top: 0,
            bottom: 0,
            width: '1px',
            bgcolor: 'rgba(0,0,0,0.14)',
            pointerEvents: 'none',
            zIndex: 1,
          }}
        />
      ))}
      {/* Overflow labels for breaks too narrow to hold text inside.
          Drawn in a second pass, above every bar, so a pill is never
          painted over by a work block that comes later in the list. */}
      {showBreakLabel &&
        segments.map((seg, i) => {
          if (seg.kind !== 'break') return null;
          const left = ((seg.start - trackStart) / trackHours) * 100;
          const width = ((seg.end - seg.start) / trackHours) * 100;
          if (width <= 0 || width > 4.5 || left >= 100) return null;
          return (
            <Box
              key={`lbl-${i}`}
              sx={{
                position: 'absolute',
                left: `${left + width / 2}%`,
                top: 0,
                height: '100%',
                transform: 'translateX(-50%)',
                display: 'flex',
                alignItems: 'center',
                px: '3px',
                borderRadius: '3px',
                // Same amber and same text treatment as a wide break, so
                // this reads as the break block itself rather than as a
                // badge sitting on top of the row. The block is drawn a
                // few pixels wider than 30 minutes strictly to fit the
                // text; the label states the real length, and the
                // tooltip gives exact start/end times.
                bgcolor: COLORS.break,
                pointerEvents: 'none',
                zIndex: 2,
              }}
            >
              <Typography
                sx={{ fontSize: 9, fontWeight: 700, color: '#5b3a06', lineHeight: 1, whiteSpace: 'nowrap' }}
              >
                {fmtDuration(seg.end - seg.start)}
              </Typography>
            </Box>
          );
        })}
    </Box>
  );
}

/** "GA 3.0% (9am their time)" -- each state carries the time in ITS OWN
 * zone, which is what makes the entry checkable: at 12pm Eastern,
 * California is only just reaching 9am. */
function statePriorityTooltip(entries: StatePriorityEntry[] | undefined, hourLabel: string): string | null {
  if (!entries || entries.length === 0) return null;
  const lines = entries
    .map((e) => {
      const when = e.local_hour != null ? ` (${fmtClock(e.local_hour)} their time)` : '';
      return `${e.state} ${(e.connect_rate * 100).toFixed(1)}%${when}`;
    })
    .join(' · ');
  return `Best states to work at ${hourLabel}, strongest first: ${lines}. Ranked by how well each connects, among states with enough leads waiting in this hour to be worth an agent's time. The same list applies every weekday.`;
}

export default function RosterGrid({
  rows,
  coverage,
  hours,
  referenceTimezone,
  statePriority,
}: {
  rows: RosterGridRow[];
  coverage: RosterGridCoverageHour[];
  hours: RosterHour[];
  referenceTimezone: string;
  statePriority?: Record<string, StatePriorityEntry[]>;
}) {
  const coverageByHour = new Map(coverage.map((c) => [c.hour, c]));
  const companyClock = companyClockLabel(referenceTimezone);
  const hasComparison = rows.some((r) => r.compareSegments && r.compareSegments.length > 0);
  const trackStart = hours.length ? hours[0].hour : 0;
  const trackHours = hours.length;
  const NAME_COL = '186px';
  const SUMMARY_COL = '150px';
  const columnTemplate = `${NAME_COL} minmax(0, 1fr) ${SUMMARY_COL}`;

  return (
    <Box>
      {/* ---- Header: hour axis + coverage numbers ---- */}
      <Box sx={{ display: 'grid', gridTemplateColumns: columnTemplate, columnGap: '8px', alignItems: 'center' }}>
        <Box />
        <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${trackHours}, 1fr)` }}>
          {hours.map((h, i) => {
            const tip = statePriorityTooltip(statePriority?.[String(h.hour)], h.hour_label);
            return (
            <Tooltip key={h.hour} title={tip ?? ''} arrow disableHoverListener={!tip}>
            <Typography
              variant="caption"
              sx={{
                fontSize: 10,
                color: tip ? 'text.primary' : 'text.secondary',
                fontWeight: tip ? 600 : 400,
                cursor: tip ? 'help' : 'default',
                textDecoration: tip ? 'underline dotted' : 'none',
                textUnderlineOffset: '3px',
                textAlign: 'center',
                // Column rules continue the ones drawn inside each
                // agent's track, so the hour label and the bar edges
                // below it visibly belong to the same column.
                borderLeft: i === 0 ? 'none' : '1px solid rgba(0,0,0,0.08)',
              }}
            >
              {h.hour_label}
            </Typography>
            </Tooltip>
            );
          })}
        </Box>
        <Box />

        <Tooltip
          arrow
          title={`How many agents are on shift each hour, against how many the recent-demand forecast calls for. Both are averages across the hour, so a half-hour of coverage counts as 0.5 — which is why these can be fractional. Hours shown on the ${referenceTimezone} company clock.`}
        >
          <Typography
            variant="caption"
            sx={{
              fontSize: 11,
              fontWeight: 600,
              lineHeight: '20px',
              textAlign: 'right',
              pr: 0.5,
              cursor: 'help',
              textDecoration: 'underline dotted',
              textUnderlineOffset: '3px',
            }}
          >
            On shift / needed
          </Typography>
        </Tooltip>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: `repeat(${trackHours}, 1fr)`,
            borderBottom: '1px solid rgba(0,0,0,0.12)',
            pb: '2px',
          }}
        >
          {hours.map((h, i) => {
            const c = coverageByHour.get(h.hour);
            const gapBad = (c?.gap ?? 0) > 2;
            return (
              <Tooltip
                key={h.hour}
                arrow
                title={
                  c
                    ? `${h.hour_label}: ${c.agents_on_shift} on shift${
                        c.agents_on_shift_before != null ? ` (${c.agents_on_shift_before} on today's schedule)` : ''
                      }, about ${c.required_agents?.toFixed(1) ?? '–'} needed — ${
                        c.gap != null && c.gap > 0
                          ? `short by about ${c.gap.toFixed(1)}`
                          : c.gap != null && c.gap < 0
                            ? `about ${Math.abs(c.gap).toFixed(1)} more than needed`
                            : 'fully staffed'
                      }`
                    : 'No coverage estimate available for this hour.'
                }
              >
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: 10,
                    textAlign: 'center',
                    lineHeight: '20px',
                    color: gapBad ? 'error.main' : 'text.secondary',
                    fontWeight: gapBad ? 700 : 400,
                    borderLeft: i === 0 ? 'none' : '1px solid rgba(0,0,0,0.08)',
                  }}
                >
                  {c ? `${c.agents_on_shift}/${c.required_agents?.toFixed(0) ?? '–'}` : '–'}
                </Typography>
              </Tooltip>
            );
          })}
        </Box>
        <Tooltip
          arrow
          title={`Each agent's optimized day, given twice: first on the ${referenceTimezone} company clock that the whole roster is planned on, then on the agent's own home clock. An amber local time means the shift starts before 7am or ends after 9pm where that agent actually lives. The third line splits the day into working time and break.`}
        >
          <Typography
            variant="caption"
            sx={{
              fontSize: 10,
              color: 'text.secondary',
              pl: 0.5,
              cursor: 'help',
              textDecoration: 'underline dotted',
              textUnderlineOffset: '3px',
            }}
          >
            {companyClock} · local · work + break
          </Typography>
        </Tooltip>
      </Box>

      {/* ---- One block per agent ---- */}
      <Box sx={{ mt: '6px' }}>
        {rows.map((agent, idx) => {
          const localOffset = localOffsetFromEastern(agent.homeTz);
          const span = segmentsSpan(agent.segments);
          const elapsed = span ? span.end - span.start : null;
          const localNote =
            localOffset != null && localOffset !== 0 && span
              ? ` Their home clock (${agent.homeTz}) is ${localOffset}h behind ${referenceTimezone}, so this is ${fmtClock(
                  span.start - localOffset,
                )}–${fmtClock(span.end - localOffset)} where they are.`
              : localOffset === 0 && agent.homeTz
                ? ` They are on the company clock (${agent.homeTz}), so these are also their local hours.`
                : '';
          const nameTooltip = span
            ? `${agent.agent_full_nm} — ${fmtClock(span.start)}–${fmtClock(span.end)} ${referenceTimezone}${
                agent.workHours != null ? `, ${fmtDuration(agent.workHours)} working` : ''
              }${agent.breakHours ? ` plus ${fmtDuration(agent.breakHours)} of break` : ''}${
                elapsed != null ? ` (${fmtDuration(elapsed)} from start to end)` : ''
              }.${localNote}${agent.tooltipExtra ? ` ${agent.tooltipExtra}` : ''}`
            : agent.agent_full_nm;

          return (
            <Box
              key={agent.agent_full_nm}
              sx={{
                display: 'grid',
                gridTemplateColumns: columnTemplate,
                columnGap: '8px',
                alignItems: 'center',
                py: '5px',
                px: '4px',
                // A ruled row, not a floating card: the bars sit in what
                // should read as a table, and a separator per row plus
                // the column rules inside each track is what supplies
                // that structure. No bottom margin, so consecutive rows
                // share an edge instead of drifting apart.
                borderBottom: '1px solid rgba(0,0,0,0.07)',
                bgcolor: idx % 2 === 1 ? 'rgba(0,0,0,0.015)' : 'transparent',
                '&:hover': { bgcolor: 'rgba(21,122,140,0.06)' },
              }}
            >
              {/* Name + provenance dot + move badge */}
              <Tooltip title={nameTooltip} arrow>
                <Stack direction="row" spacing={0.5} alignItems="center" sx={{ overflow: 'hidden', cursor: 'help' }}>
                  <Box
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      flexShrink: 0,
                      bgcolor: agent.source === 'real' ? 'success.main' : 'text.disabled',
                    }}
                  />
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: 11,
                      color: agent.sufficient_history ? 'text.primary' : 'text.disabled',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {agent.agent_full_nm}
                  </Typography>
                  {agent.badge && (
                    <Chip
                      label={agent.badge}
                      size="small"
                      variant="outlined"
                      color={agent.badge === 'unchanged' ? 'default' : 'warning'}
                      sx={{ height: 16, flexShrink: 0, '& .MuiChip-label': { fontSize: 9, px: 0.5 } }}
                    />
                  )}
                </Stack>
              </Tooltip>

              {/* Timeline */}
              {!agent.sufficient_history ? (
                <Tooltip title={`${agent.agent_full_nm}: not enough recent history to infer a shift`} arrow>
                  <Box
                    sx={{
                      height: 22,
                      borderRadius: '3px',
                      bgcolor: '#f2f1ec',
                      backgroundImage:
                        'repeating-linear-gradient(45deg, rgba(0,0,0,0.05) 0, rgba(0,0,0,0.05) 2px, transparent 2px, transparent 6px)',
                    }}
                  />
                </Tooltip>
              ) : agent.compareSegments && agent.compareSegments.length > 0 ? (
                <Stack spacing="2px">
                  <ShiftTrack
                    segments={agent.compareSegments}
                    trackStart={trackStart}
                    trackHours={trackHours}
                    height={10}
                    workColor={COLORS.workToday}
                    agentName={agent.agent_full_nm}
                    scheduleLabel="today's schedule"
                    referenceTimezone={referenceTimezone}
                    localOffset={localOffset}
                    showBreakLabel={false}
                  />
                  <ShiftTrack
                    segments={agent.segments}
                    trackStart={trackStart}
                    trackHours={trackHours}
                    height={14}
                    workColor={COLORS.workOptimized}
                    agentName={agent.agent_full_nm}
                    scheduleLabel="optimized"
                    referenceTimezone={referenceTimezone}
                    localOffset={localOffset}
                    showBreakLabel
                  />
                </Stack>
              ) : (
                <ShiftTrack
                  segments={agent.segments}
                  trackStart={trackStart}
                  trackHours={trackHours}
                  height={22}
                  workColor={COLORS.workOptimized}
                  agentName={agent.agent_full_nm}
                  scheduleLabel="optimized"
                  referenceTimezone={referenceTimezone}
                  localOffset={localOffset}
                  showBreakLabel
                />
              )}

              {/* Plain-language summary: the numbers the bars can only imply */}
              <Box sx={{ overflow: 'hidden' }}>
                {/* Company clock first, the agent's own clock second,
                    ALWAYS both and always in that order -- including for
                    Eastern agents, where the two lines are identical.
                    Showing the local line only when it differed made a
                    bare "9am-7pm" ambiguous (company or local?) and gave
                    rows two different heights depending on timezone. */}
                {span && (
                  <Typography sx={{ fontSize: 10.5, fontWeight: 600, lineHeight: 1.35, whiteSpace: 'nowrap' }}>
                    {fmtClock(span.start)}–{fmtClock(span.end)}
                    <Box component="span" sx={{ fontWeight: 400, color: 'text.secondary' }}>
                      {' '}
                      {companyClock}
                    </Box>
                  </Typography>
                )}
                {span && localOffset != null && (
                  <Typography
                    sx={{
                      fontSize: 10.5,
                      fontWeight: 600,
                      lineHeight: 1.35,
                      whiteSpace: 'nowrap',
                      color: isUnsociableLocal(span.start - localOffset, span.end - localOffset)
                        ? 'warning.dark'
                        : 'text.primary',
                    }}
                  >
                    {fmtClock(span.start - localOffset)}–{fmtClock(span.end - localOffset)}
                    <Box component="span" sx={{ fontWeight: 400, color: 'text.secondary' }}>
                      {' '}
                      {agent.homeTz}
                    </Box>
                  </Typography>
                )}
                {agent.workHours != null && (
                  <Typography sx={{ fontSize: 9.5, color: 'text.secondary', lineHeight: 1.35, whiteSpace: 'nowrap' }}>
                    {fmtDuration(agent.workHours)} work
                    {agent.breakHours ? ` + ${fmtDuration(agent.breakHours)} break` : ''}
                  </Typography>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>

      {/* ---- Legend ---- */}
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 1.5, flexWrap: 'wrap', rowGap: 0.5 }}>
        {hasComparison && (
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Box sx={{ width: 14, height: 8, borderRadius: '2px', bgcolor: COLORS.workToday }} />
            <Typography variant="caption" color="text.secondary">
              Working today (thin top bar)
            </Typography>
          </Stack>
        )}
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Box sx={{ width: 14, height: 10, borderRadius: '2px', bgcolor: COLORS.workOptimized }} />
          <Typography variant="caption" color="text.secondary">
            Working, optimized{hasComparison ? ' (thick bar)' : ''}
          </Typography>
        </Stack>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Box
            sx={{
              width: 14,
              height: 10,
              borderRadius: '2px',
              bgcolor: COLORS.break,
              boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.85)',
            }}
          />
          <Typography variant="caption" color="text.secondary">
            Break — drawn to length, labelled when it fits
          </Typography>
        </Stack>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Box sx={{ width: 14, height: 10, borderRadius: '2px', bgcolor: COLORS.track }} />
          <Typography variant="caption" color="text.secondary">
            Off
          </Typography>
        </Stack>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'success.main' }} />
          <Typography variant="caption" color="text.secondary">
            Real schedule
          </Typography>
        </Stack>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'text.disabled' }} />
          <Typography variant="caption" color="text.secondary">
            Inferred from call history
          </Typography>
        </Stack>
        <Typography variant="caption" color="text.secondary">
          {`· clock: ${referenceTimezone}`}
        </Typography>
        {statePriority && Object.keys(statePriority).length > 0 && (
          <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 600 }}>
            · hover an hour above to see which states to work in it
          </Typography>
        )}
      </Stack>
    </Box>
  );
}
